import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../lib/constants';
import { getCookie } from '../../lib/utils';
import {
  MdSchedule,
  MdAccessTime,
  MdPeople,
  MdCheckCircle,
  MdAssignment,
  MdCalendarToday,
  MdCheck,
} from 'react-icons/md';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from "src/components/ui/card";
import { Button } from '@mui/material';

import { AuthContext } from '../../contexts/AuthContext';
import { useContext } from 'react';

const DoctorDashboard = () => {
  const { fullname, lastlogin } = useContext(AuthContext);
  const [todayOps, setTodayOps] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const token = getCookie('csrf_access_token');
      // Fetch today's surgeries
      const opsRes = await axios.get(`${API_BASE_URL}/today_operations`, { 
        withCredentials: true,
        headers: { 'X-CSRF-TOKEN': token }
      });
      if (opsRes.data.status === 'success' || opsRes.data.data) {
        const d = opsRes.data.data || opsRes.data;
        const sorted = (Array.isArray(d) ? d : []).sort((a, b) => (a.time || '').localeCompare(b.time || ''));
        setTodayOps(sorted);
      }

      // Fetch all preop orders
      const preopRes = await axios.get(`${API_BASE_URL}/list_preop_orders`, { 
        withCredentials: true,
        headers: { 'X-CSRF-TOKEN': token }
      });
      const preopData = preopRes.data.data || preopRes.data;
      if (Array.isArray(preopData)) {
        setData(preopData);
      }
    } catch (err) {
      console.error("Error fetching doctor dashboard data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
    const interval = setInterval(fetchAllData, 30000); // Auto-refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Calculate my patients more robustly
  const myPatientsCount = data.filter(preop => {
    if (!preop.surgeonName || !fullname) return false;
    const nameA = preop.surgeonName.toLowerCase().replace('dr.', '').trim();
    const nameB = fullname.toLowerCase().replace('dr.', '').trim();
    return nameA.includes(nameB) || nameB.includes(nameA);
  }).length;

  const nextSurgery = todayOps && todayOps.length > 0 ? todayOps[0] : null;

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm relative overflow-hidden group">
        <div className="absolute right-0 top-0 w-32 h-32 bg-teal-50 rounded-bl-full -z-0 transition-transform group-hover:scale-110 duration-500"></div>
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-black text-slate-800 tracking-tight mb-2">
              Welcome back, <span className="text-teal-600">Dr. {fullname || 'Doctor'}</span>
            </h1>
            <div className="flex items-center space-x-4 text-sm font-bold text-slate-400 uppercase tracking-widest">
              <div className="flex items-center group/item">
                <MdAccessTime className="w-4 h-4 mr-2 text-teal-500 group-hover/item:rotate-12 transition-transform" />
                <span>Last login: {lastlogin || 'Just now'}</span>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end">
            <p className="text-xs font-black text-teal-600 bg-teal-50 px-3 py-1 rounded-full uppercase tracking-tighter mb-1">Status</p>
            <div className="flex items-center">
              <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full mr-2 shadow-[0_0_8px_rgba(16,185,129,0.5)]"></div>
              <span className="text-sm font-black text-slate-700">Online & Ready</span>
            </div>
          </div>
        </div>
      </div>

      {/* Top stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-r from-indigo-500 to-indigo-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <MdSchedule className="w-6 h-6" />
              <div>
                <p className="text-indigo-100 text-sm">My Surgeries Today</p>
                <h3 className="text-2xl font-bold">{todayOps.length}</h3>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-teal-500 to-teal-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <MdAccessTime className="w-6 h-6" />
              <div>
                <p className="text-teal-100 text-sm">Next Surgery</p>
                <h3 className="text-xl font-bold">{nextSurgery?.time || '--:--'}</h3>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <MdPeople className="w-6 h-6" />
              <div>
                <p className="text-orange-100 text-sm">My Patients</p>
                <h3 className="text-2xl font-bold">
                  {myPatientsCount}
                </h3>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-pink-500 to-pink-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <MdCheckCircle className="w-6 h-6" />
              <div>
                <p className="text-pink-100 text-sm">Completed Today</p>
                <h3 className="text-2xl font-bold">
                  {todayOps.filter(op => op.status === 'completed').length}
                </h3>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Surgery schedule */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MdCalendarToday className="text-indigo-600" />
            My Surgery Schedule - Today
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p>Loading...</p>
          ) : (
            <div className="space-y-4">
              {todayOps.map((surgery, idx) => (
                <div key={idx} className={`p-4 rounded-lg border-l-4 ${surgery.status === 'upcoming' ? 'bg-blue-50 border-blue-500' :
                  surgery.status === 'ongoing' ? 'bg-green-50 border-green-500' :
                    'bg-gray-50 border-gray-400'
                  }`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-2">
                        <span className="font-bold text-lg">{surgery.time}</span>
                        <div className={`px-2 py-1 rounded-full text-xs font-semibold ${surgery.status === 'ongoing' ? 'bg-green-100 text-green-800' :
                          surgery.status === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                          {surgery.status.toUpperCase()}
                        </div>
                      </div>
                      <h4 className="font-semibold text-gray-800">{surgery.patient}</h4>
                      <p className="text-gray-600">{surgery.surgery}</p>
                      <div className="flex gap-4 mt-2 text-sm text-gray-500">
                        <span>{surgery.ot}</span>
                      </div>
                    </div>
                    {surgery.status === 'scheduled' && (
                      <Button className="bg-indigo-600 hover:bg-indigo-700 text-white">
                        Start Surgery
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Next patient full details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg font-semibold text-indigo-700">
            <MdAssignment className="text-indigo-600" />
            All Patients - Detailed Preop Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {data.map((preop, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-2xl p-5 shadow-sm bg-white hover:shadow-md transition-shadow"
              >
                {/* Patient Info */}
                <h4 className="font-semibold text-indigo-600 mb-3">Patient Info</h4>
                <div className="space-y-1 text-sm text-gray-700">
                  <div className="flex justify-between"><span>Name:</span><span>{preop.name}</span></div>
                  <div className="flex justify-between"><span>UHID:</span><span>{preop.uhid}</span></div>
                  <div className="flex justify-between"><span>Age:</span><span>{preop.age}</span></div>
                  <div className="flex justify-between"><span>Gender:</span><span>{preop.sex}</span></div>
                </div>

                {/* Surgery Info */}
                <h4 className="font-semibold text-indigo-600 mt-4 mb-3">Surgery Info</h4>
                <div className="space-y-1 text-sm text-gray-700">
                  <div className="flex justify-between"><span>Diagnosis:</span><span>{preop.diagnosis}</span></div>
                  <div className="flex justify-between"><span>Proposed:</span><span>{preop.proposedSurgery}</span></div>
                  <div className="flex justify-between"><span>Room:</span><span>{preop.room}</span></div>
                  <div className="flex justify-between"><span>Surgeon:</span><span>{preop.surgeonName}</span></div>
                </div>

                {/* Status */}
                <div className="mt-4">
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold
              ${preop.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      preop.status === 'reviewed' ? 'bg-green-100 text-green-800' :
                        preop.status === 'rejected' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                    }`}>
                    Status: {preop.status.toUpperCase()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>


    </div>
  );
};

export default DoctorDashboard;
